import { Controller } from "@hotwired/stimulus";

const DEFAULT_PLACEHOLDER = "What do you need help with?";
const DSE_PLACEHOLDER = "How can we help with your evaluation trial?";

const TYPE_PLACEHOLDERS: Record<string, string> = {
  bug: "What happened? What did you expect to happen?",
  billing: "What's your billing question?",
  feedback: "What feature or improvement would you like to see?",
  general: DEFAULT_PLACEHOLDER,
};

const DRAG_ACTIVE_CLASSES = ["border-lime-500", "bg-lime-50", "text-lime-700"];
const DRAG_INACTIVE_CLASSES = ["border-gray-300", "text-charcoal-600"];

const TOAST_BASE_CLASSES = ["border-l-4", "p-4", "rounded"];
const TOAST_SUCCESS_CLASSES = ["bg-green-500", "text-black", "border-green-700"];
const TOAST_ERROR_CLASSES = ["bg-red-500", "text-white", "border-red-700"];

export default class extends Controller {
  static targets = [
    "formPanel",
    "dseEyebrow",
    "supportTypeField",
    "subjectField",
    "messageField",
    "submitButton",
    "toast",
    "actualForm",
    "fileInput",
    "fileList",
    "uploadingRowTemplate",
    "attachmentRowTemplate",
    "attachmentIdsField",
    "dropZone",
  ];

  static values = {
    organizationId: String,
    uploadUrl: String,
    onEvaluationPlan: Boolean,
  };

  declare readonly organizationIdValue: string;
  declare readonly uploadUrlValue: string;
  declare readonly onEvaluationPlanValue: boolean;

  declare readonly formPanelTarget: HTMLElement;
  declare readonly dseEyebrowTarget: HTMLElement;
  declare readonly supportTypeFieldTarget: HTMLSelectElement;
  declare readonly subjectFieldTarget: HTMLInputElement;
  declare readonly messageFieldTarget: HTMLTextAreaElement;
  declare readonly submitButtonTarget: HTMLButtonElement;
  declare readonly toastTarget: HTMLElement;
  declare readonly actualFormTarget: HTMLFormElement;
  declare readonly fileInputTarget: HTMLInputElement;
  declare readonly fileListTarget: HTMLElement;
  declare readonly uploadingRowTemplateTarget: HTMLTemplateElement;
  declare readonly attachmentRowTemplateTarget: HTMLTemplateElement;
  declare readonly attachmentIdsFieldTarget: HTMLInputElement;
  declare readonly dropZoneTarget: HTMLElement;

  private isSubmitting = false;
  private formActive = false;
  private uploadingCount = 0;
  private uploadGeneration = 0;
  private pendingSubject: string | null = null;
  private activeSubject: string | null = null;
  private uploadedAttachments: Array<{ id: string; filename: string; size: number }> = [];
  private uploadingFiles: Array<{
    tempId: string;
    filename: string;
    size: number;
  }> = [];
  private tempIdCounter = 0;
  // NOTE: These values mirror AttachmentValidator (app/models/attachment_validator.rb).
  // Update both if limits change. Plain caps email attachments at 6 MB combined.
  private readonly BYTES_PER_KB = 1024;
  private readonly BYTES_PER_MB = 1024 * 1024;
  private readonly MAX_FILE_SIZE = 6 * this.BYTES_PER_MB; // 6MB per file
  private readonly MAX_TOTAL_SIZE = 6 * this.BYTES_PER_MB; // 6MB combined (Plain email limit)
  private readonly MAX_FILES = 5;
  private boundHandleDrawerToggle: (event: Event) => void = this.handleDrawerToggle.bind(this);
  private boundCaptureTrigger = this.captureTrigger.bind(this);

  connect() {
    const drawer = document.getElementById("support-drawer");
    if (drawer) {
      drawer.addEventListener("toggle", this.boundHandleDrawerToggle);
    }

    // Capture trigger button data BEFORE the popover steals focus
    document.addEventListener("click", this.boundCaptureTrigger, true);
  }

  disconnect() {
    const drawer = document.getElementById("support-drawer");
    if (drawer) {
      drawer.removeEventListener("toggle", this.boundHandleDrawerToggle);
    }
    document.removeEventListener("click", this.boundCaptureTrigger, true);
  }

  // Capture click on any trigger button before the popover opens
  private captureTrigger(event: Event) {
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>(
      '[popovertarget="support-drawer"]',
    );
    if (button) {
      this.pendingSubject = button.dataset.subject || null;
    }
  }

  private handleDrawerToggle(event: Event) {
    const drawer = event.target as HTMLElement & { matches: (selector: string) => boolean };

    if (!drawer.matches(":popover-open")) {
      return;
    }

    const triggerSubject = this.pendingSubject;
    this.pendingSubject = null;

    this.showFormPanel();

    // If a request with the same subject is already in progress (e.g. the user closed
    // the drawer by clicking outside and reopened it via the same trigger), preserve it
    // instead of resetting. Match on subject so switching to a different request resets.
    if (this.formActive && this.activeSubject === triggerSubject) {
      return;
    }

    this.resetForm();
    this.transitionToForm();
    if (triggerSubject) {
      this.subjectFieldTarget.value = triggerSubject;
    }
    this.activeSubject = triggerSubject;
  }

  private showFormPanel() {
    this.formPanelTarget.classList.remove("hidden");
    this.formPanelTarget.classList.add("flex");
  }

  // Called by the cancel button and after a successful submit — both are an explicit
  // end to the request, so the in-progress input is cleared (unlike clicking outside
  // to close the drawer, which preserves it).
  closeDrawer() {
    const drawer = document.getElementById("support-drawer") as HTMLElement & {
      hidePopover?: () => void;
    };
    drawer?.hidePopover?.();
    this.resetForm();
    this.formActive = false;
  }

  private transitionToForm() {
    this.dseEyebrowTarget.classList.toggle("hidden", !this.onEvaluationPlanValue);

    this.updatePlaceholder();

    this.showFormPanel();
    this.formActive = true;
  }

  updatePlaceholder() {
    const type = this.supportTypeFieldTarget.value;
    const fallback = this.onEvaluationPlanValue ? DSE_PLACEHOLDER : DEFAULT_PLACEHOLDER;
    this.messageFieldTarget.placeholder = TYPE_PLACEHOLDERS[type] || fallback;
  }

  handleSubmit(event: Event) {
    event.preventDefault();
    if (this.isSubmitting || this.uploadingCount > 0) {
      return;
    }

    const form = event.target as HTMLFormElement;
    this.setSubmitting(true);

    fetch(form.action, {
      method: form.method,
      body: new FormData(form),
      headers: {
        "X-CSRF-Token": this.csrfToken(),
        Accept: "application/json",
      },
    })
      .then((response) =>
        response.ok
          ? response.json()
          : response
              .json()
              .catch(() => ({ success: false, error: `Server error: ${response.status}` })),
      )
      .then((data) => this.handleSubmitResponse(data))
      .catch(() => this.showToast("Oops, something went wrong. Please try again.", "error"))
      .finally(() => this.setSubmitting(false));
  }

  private setSubmitting(value: boolean) {
    this.isSubmitting = value;
    this.refreshSubmitButtonState();
    if (value) {
      this.submitButtonTarget.dataset.originalText =
        this.submitButtonTarget.textContent || "Send request";
      this.submitButtonTarget.textContent = "Submitting...";
    } else {
      this.submitButtonTarget.textContent =
        this.submitButtonTarget.dataset.originalText || "Send request";
    }
  }

  private handleSubmitResponse(data: {
    success: boolean;
    message?: string;
    errors?: string[];
    error?: string;
  }) {
    if (data.success) {
      this.showToast(data.message || "Thanks! We'll be in touch shortly.", "success");
      setTimeout(() => this.closeDrawer(), 2000);
    } else {
      const errorMessage =
        data.errors && Array.isArray(data.errors)
          ? data.errors.join(", ")
          : data.error || "Oops, something went wrong. Please try again.";
      this.showToast(errorMessage, "error");
    }
  }

  private resetForm() {
    this.uploadGeneration += 1;
    this.actualFormTarget.reset();
    this.supportTypeFieldTarget.value = "";
    this.activeSubject = null;
    this.toastTarget.classList.add("hidden");

    // Clear uploaded attachments
    this.uploadedAttachments = [];
    this.uploadingFiles = [];
    this.updateAttachmentsList();
    this.refreshSubmitButtonState();
  }

  // File upload handlers

  async handleFileSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);

    await this.processFiles(files);
    input.value = ""; // Clear input for next selection
  }

  handleDragOver(_dragEvent: DragEvent) {
    this.setDropZoneActive(true);
  }

  handleDragLeave(event: DragEvent) {
    if (this.dropZoneTarget.contains(event.relatedTarget as Node)) {
      return;
    }
    this.resetDropZoneStyle();
  }

  async handleDrop(event: DragEvent) {
    this.resetDropZoneStyle();

    const files = Array.from(event.dataTransfer?.files || []);
    await this.processFiles(files);
  }

  removeAttachment(event: Event) {
    const button = event.currentTarget as HTMLButtonElement;
    const index = parseInt(button.dataset.index || "0", 10);

    this.uploadedAttachments = this.uploadedAttachments.filter(
      (_attachment, attachmentIndex) => attachmentIndex !== index,
    );
    this.updateAttachmentsList();
  }

  showToast(message: string, type: "success" | "error") {
    const toast = this.toastTarget;
    toast.textContent = message;
    toast.classList.remove(
      "hidden",
      ...TOAST_BASE_CLASSES,
      ...TOAST_SUCCESS_CLASSES,
      ...TOAST_ERROR_CLASSES,
    );

    toast.classList.add(
      ...TOAST_BASE_CLASSES,
      ...(type === "success" ? TOAST_SUCCESS_CLASSES : TOAST_ERROR_CLASSES),
    );

    // Toast appended after a long form can render below the fold, bring it into view.
    toast.scrollIntoView({ behavior: "smooth", block: "nearest" });

    setTimeout(() => {
      toast.classList.add("hidden");
    }, 3000);
  }

  private validateFile(file: File): boolean {
    if (file.size > this.MAX_FILE_SIZE) {
      this.showToast(
        `File "${file.name}" is too large (max ${this.MAX_FILE_SIZE / this.BYTES_PER_MB}MB)`,
        "error",
      );
      return false;
    }

    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
      "application/pdf",
      "text/plain",
      "text/csv",
      "application/json",
      "application/zip",
      "application/x-zip-compressed",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    if (!allowedTypes.includes(file.type)) {
      this.showToast(`File type "${file.type}" is not allowed`, "error");
      return false;
    }

    return true;
  }

  private async processFiles(files: File[]) {
    if (files.length === 0) {
      return;
    }

    // If a batch is already uploading, uploadedAttachments is stale and the count/size
    // checks below cannot be trusted. Reject the new batch with a clear message.
    if (this.uploadingCount > 0) {
      this.showToast(
        "Please wait for the current upload to finish before adding more files",
        "error",
      );
      return;
    }

    // Filter invalid files first so the count check reflects only the files that would
    // actually be uploaded — avoids rejecting valid files when some in the batch are invalid.
    const validFiles = files.filter((file) => this.validateFile(file));
    if (validFiles.length === 0) {
      return;
    }

    if (this.uploadedAttachments.length + validFiles.length > this.MAX_FILES) {
      this.showToast(`You can only upload up to ${this.MAX_FILES} files`, "error");
      return;
    }

    const currentTotal = this.uploadedAttachments.reduce((sum, att) => sum + att.size, 0);
    const newTotal = validFiles.reduce((sum, file) => sum + file.size, 0);
    if (currentTotal + newTotal > this.MAX_TOTAL_SIZE) {
      this.showToast(
        `Total attachments cannot exceed ${this.MAX_TOTAL_SIZE / this.BYTES_PER_MB}MB`,
        "error",
      );
      return;
    }

    await this.uploadFiles(validFiles);
  }

  private async uploadFiles(files: File[]) {
    this.uploadingCount += 1;
    this.refreshSubmitButtonState();

    const generation = this.uploadGeneration;

    const queued = files.map((file) => ({
      tempId: `upload-${this.tempIdCounter++}`,
      filename: file.name,
      size: file.size,
    }));
    this.uploadingFiles.push(...queued);
    this.updateAttachmentsList();

    // Uploads run concurrently — each call guards itself against a stale generation
    await Promise.all(
      files.map(async (file, index) => {
        const tempId = queued[index].tempId;
        try {
          await this.uploadFile(file, generation, tempId);
        } catch (error) {
          this.showToast(`Failed to upload ${file.name}`, "error");
          this.uploadingFiles = this.uploadingFiles.filter((upload) => upload.tempId !== tempId);
          this.updateAttachmentsList();
        }
      }),
    );

    this.uploadingCount -= 1;
    this.refreshSubmitButtonState();
  }

  private refreshSubmitButtonState() {
    this.submitButtonTarget.disabled = this.isSubmitting || this.uploadingCount > 0;
  }

  private async uploadFile(file: File, generation: number, tempId: string): Promise<void> {
    const uploadUrlData = await this.getUploadUrl(file);
    if (this.uploadGeneration !== generation) {
      return;
    }

    await this.uploadToS3(file, uploadUrlData);
    if (this.uploadGeneration !== generation) {
      return;
    }

    // Hold briefly so the uploading row is visibly shown before swapping to the
    // finished-attachment row — without this a fast upload replaces the row instantly.
    await this.wait(300);
    if (this.uploadGeneration !== generation) {
      return;
    }

    this.uploadedAttachments.push({
      id: uploadUrlData.attachment_id,
      filename: file.name,
      size: file.size,
    });
    this.uploadingFiles = this.uploadingFiles.filter((upload) => upload.tempId !== tempId);

    this.updateAttachmentsList();
  }

  private wait(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private async getUploadUrl(file: File): Promise<{
    attachment_id: string;
    upload_form_url: string;
    upload_form_data?: Array<{ key: string; value: string }>;
  }> {
    const response = await fetch(this.uploadUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken(),
        Accept: "application/json",
      },
      body: JSON.stringify({
        filename: file.name,
        content_type: file.type,
        file_size: file.size,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to get upload URL");
    }

    return response.json();
  }

  private uploadToS3(
    file: File,
    uploadData: {
      upload_form_url: string;
      upload_form_data?: Array<{ key: string; value: string }>;
    },
  ): Promise<void> {
    const formData = new FormData();

    if (uploadData.upload_form_data) {
      uploadData.upload_form_data.forEach((field) => {
        formData.append(field.key, field.value);
      });
    }

    formData.append("file", file);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", uploadData.upload_form_url);

      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed: ${xhr.status}`));
        }
      });

      xhr.addEventListener("error", () => reject(new Error("Upload failed")));

      xhr.send(formData);
    });
  }

  private updateAttachmentsList() {
    const ids = this.uploadedAttachments.map((attachment) => attachment.id);
    this.attachmentIdsFieldTarget.value = JSON.stringify(ids);

    this.fileListTarget.replaceChildren();

    const hasItems = this.uploadingFiles.length > 0 || this.uploadedAttachments.length > 0;
    this.fileListTarget.classList.toggle("hidden", !hasItems);
    if (!hasItems) {
      return;
    }

    for (const uploading of this.uploadingFiles) {
      this.fileListTarget.appendChild(this.buildUploadingRow(uploading));
    }

    for (const [index, attachment] of this.uploadedAttachments.entries()) {
      this.fileListTarget.appendChild(this.buildAttachmentRow(attachment, index));
    }
  }

  private buildUploadingRow(uploading: {
    tempId: string;
    filename: string;
    size: number;
  }): HTMLElement {
    const row = this.cloneTemplate(this.uploadingRowTemplateTarget);
    row.dataset.tempId = uploading.tempId;

    this.queryRole(row, "filename").textContent = uploading.filename;
    this.queryRole(row, "size").textContent = this.formatFileSize(uploading.size);

    return row;
  }

  private buildAttachmentRow(
    attachment: { id: string; filename: string; size: number },
    index: number,
  ): HTMLElement {
    const row = this.cloneTemplate(this.attachmentRowTemplateTarget);

    this.queryRole(row, "filename").textContent = attachment.filename;
    this.queryRole(row, "size").textContent = this.formatFileSize(attachment.size);

    const button = this.queryRole<HTMLButtonElement>(row, "removeButton");
    button.dataset.index = String(index);
    button.setAttribute("aria-label", `Remove ${attachment.filename}`);

    return row;
  }

  private cloneTemplate(template: HTMLTemplateElement): HTMLElement {
    const root = template.content.firstElementChild;
    if (!root) {
      throw new Error("Template is missing its root element");
    }
    return root.cloneNode(true) as HTMLElement;
  }

  private queryRole<T extends HTMLElement = HTMLElement>(root: HTMLElement, role: string): T {
    const element = root.querySelector<T>(`[data-role="${role}"]`);
    if (!element) {
      throw new Error(`Missing element with data-role="${role}"`);
    }
    return element;
  }

  private formatFileSize(bytes: number): string {
    if (bytes < this.BYTES_PER_KB) {
      return `${bytes} B`;
    }
    if (bytes < this.BYTES_PER_MB) {
      return `${(bytes / this.BYTES_PER_KB).toFixed(1)} KB`;
    }
    return `${(bytes / this.BYTES_PER_MB).toFixed(1)} MB`;
  }

  private resetDropZoneStyle() {
    this.setDropZoneActive(false);
  }

  private setDropZoneActive(active: boolean) {
    this.dropZoneTarget.classList.remove(...(active ? DRAG_INACTIVE_CLASSES : DRAG_ACTIVE_CLASSES));
    this.dropZoneTarget.classList.add(...(active ? DRAG_ACTIVE_CLASSES : DRAG_INACTIVE_CLASSES));
  }

  private csrfToken(): string {
    return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "";
  }
}
